import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useNavigate } from 'react-router-dom';

const ACTION_STEPS = [
  { id: 1, text: "Scanning 275M+ B2B Contacts...", icon: "Search", color: "text-blue-400" },
  { id: 2, text: "Found 43 highly targeted leads", icon: "Users", color: "text-green-400" },
  { id: 3, text: "AI generating personalized sequences...", icon: "Sparkles", color: "text-purple-400" },
  { id: 4, text: "Sending outreach...", icon: "Send", color: "text-yellow-400" },
  { id: 5, text: "Meeting Booked: Michael Chen (VP Sales)", icon: "CalendarCheck", color: "text-primary" }
];

const HeroSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % (ACTION_STEPS.length + 1));
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-background pt-24 pb-16">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -right-[20%] w-[60%] h-[60%] rounded-full bg-secondary/10 blur-[150px]" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left Column - Copy & CTA */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-start text-left"
          >
            <div className="inline-flex items-center space-x-2 glassmorphism rounded-full px-4 py-1.5 mb-6 border border-primary/20">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse shadow-[0_0_8px_var(--color-primary)]" />
              <span className="text-sm font-body-semibold text-primary">
                Live: AI SDR Engine Active
              </span>
            </div>

            <h1 className="font-headline-black text-5xl sm:text-6xl lg:text-7xl text-foreground mb-6 leading-[1.1] tracking-tight">
             Turn Raw Leads Into Booked Demos<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
               On Autopilot
              </span>
            </h1>

          
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-lg leading-relaxed">
              The first AI-powered outbound engine that researches, writes, and books meetings for you. Scale your sales without scaling your team.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                onClick={() => navigate('/login')}
                    variant="default"
                    size="lg"
                    iconName="Zap"
                    iconPosition="right"
                    className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 border-0 shadow-lg shadow-primary/20 text-white font-body-bold h-14 px-8 rounded-xl transition-all hover:scale-[1.02]"
                  >
                    Deploy AI SDR
                  </Button>
            </div>
          </motion.div>


          {/* Right Column - Active Simulation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:h-[500px] w-full flex items-center justify-center"
          >
            {/* Mock Dashboard Window */}
            <div className="w-full max-w-lg glassmorphism rounded-2xl border border-border/50 shadow-2xl overflow-hidden bg-background/40 backdrop-blur-xl">
              {/* Window Header */}
              <div className="flex items-center px-4 py-3 border-b border-border/50 bg-card/30">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <div className="w-3 h-3 rounded-full bg-success" />
                </div>
                <div className="mx-auto text-xs font-body-medium text-muted-foreground flex items-center">
                  <Icon name="Activity" size={14} className="mr-2 text-primary animate-pulse" />
                  Campaign Execution Log
                </div>
              </div>

              {/* Window Body */}
              <div className="p-6 h-[350px] flex flex-col justify-end relative">

                {/* Floating "Targeting" element */}
                <div className="absolute top-6 right-6 opacity-50 text-xs text-right space-y-1">
                  <div className="font-mono text-muted-foreground">TARGET_ICP="SaaS Founders"</div>
                  <div className="font-mono text-muted-foreground">REGION="US"</div>
                  <div className="font-mono text-muted-foreground">COMPANY_SIZE="10-50"</div>
                </div>

                <div className="space-y-4 font-mono text-sm">
                  <AnimatePresence>
                    {ACTION_STEPS.map((step, index) => (
                      index < activeStep && (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center space-x-3 bg-card/50 p-3 rounded-lg border border-border/50"
                        >
                          <div className={`shrink-0 ${step.color}`}>
                            <Icon name={step.icon} size={18} />
                          </div>
                          <span className="text-foreground">{step.text}</span>
                        </motion.div>
                      )
                    ))}
                  </AnimatePresence>

                  {activeStep < ACTION_STEPS.length && (
                    <motion.div
                      key="typing"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center space-x-2 text-muted-foreground p-3"
                    >
                      <div className="w-1.5 h-4 bg-primary animate-pulse" />
                      <span>{activeStep === 0 ? "Initializing engine..." : "Processing..."}</span>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Decorative elements behind the mock window */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-3xl -z-10" />
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-secondary/20 rounded-full blur-3xl -z-10" />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
